
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, LanguageContextType } from '@/types/language';
import { translations } from '@/translations';
import { getTextDirection, getSavedLanguage } from '@/utils/languageUtils';
import { useLanguages } from '@/hooks/useLanguages';

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: () => '',
  dir: 'ltr',
  availableLanguages: []
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { languages: dbLanguages, loading, error } = useLanguages(true); // Get only active languages
  const [language, setLanguage] = useState<Language>(() => {
    // Initialize with a safe default first
    return 'en';
  });
  
  const dir = getTextDirection(language);
  
  // Convert database languages to the format expected by the UI
  const availableLanguages = dbLanguages.map(lang => ({
    code: lang.code as Language,
    name: lang.name,
    nativeName: lang.native_name,
    flagIcon: lang.flag_icon
  }));
  
  const t = (key: string) => {
    return translations[language][key] || key;
  };
  
  // Function to change language
  const changeLanguage = (lang: Language) => {
    console.log(`[LanguageContext] Changing language to: ${lang}`);
    setLanguage(lang);
    localStorage.setItem('museum-language', lang);
  };
  
  // Initialize language preference when database languages are loaded
  useEffect(() => {
    if (!loading && availableLanguages.length > 0) {
      console.log(`[LanguageContext] Database languages loaded: ${availableLanguages.map(l => l.code).join(', ')}`);
      
      // Try to get saved language or detect from browser
      const availableCodes = availableLanguages.map(lang => lang.code);
      const preferredLang = getSavedLanguage(availableCodes);
      
      console.log(`[LanguageContext] Preferred language detected: ${preferredLang}`);
      
      // Only update if the preferred language is different and available
      if (preferredLang !== language && availableCodes.includes(preferredLang)) {
        setLanguage(preferredLang);
      }
      
      // Handle case where current language becomes inactive
      if (!availableLanguages.some(l => l.code === language)) {
        const fallbackLang = availableLanguages[0]?.code || 'en';
        console.log(`[LanguageContext] Current language ${language} not available, falling back to: ${fallbackLang}`);
        changeLanguage(fallbackLang);
      }
    }
    
    if (error) {
      console.warn('[LanguageContext] Error loading languages from database, using fallback');
    }
  }, [loading, availableLanguages.length, error]); // Stable dependencies
  
  useEffect(() => {
    // Update HTML dir attribute when language changes
    document.documentElement.dir = dir;
    console.log(`[LanguageContext] Updated document direction to: ${dir}`);
  }, [language, dir]);
  
  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage, 
      t, 
      dir,
      availableLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
